import StreamContext from 'shared/contexts/StreamContext'
import {
  FC,
  ReactNode,
  useState,
  useEffect,
  useCallback,
  useRef,
  useContext
} from 'react'
import jsLogger from 'js-logger'
import set from 'lodash/set'
import get from 'lodash/get'
import find from 'lodash/find'
import { ControlT } from 'types/internal'
import RVideo from 'shared/components/recorder/Rvideo'
import { requestMediaPermissions, MediaPermissionsErrorType } from 'mic-check'
import { useGetSet } from 'react-use'
import SessionVideoRecorderContext from 'shared/contexts/SessionVideoRecorderContext'
import useMicHealth from 'shared/utils/useMicHealth'

export enum PermissionErrorType {
  explanation = 'explanation',
  systemDenied = 'systemDenied',
  userDenied = 'userDenied',
  trackError = 'trackError'
}

type GrantedPermissions = {
  camera: boolean
  microphone: boolean
}

type Props = {
  children: ReactNode
  onError: (error: string) => void
  isLazy?: boolean
  onPermissionsError: (permissionsError: PermissionErrorType) => void
  // Client-side user-video recording sink. When provided, the user camera is
  // recorded locally (RVideo) and each chunk is handed here (legacy client
  // recording — demo3 / flow sessions). Omit it for the server-side recording
  // scheme (Demo2), where the camera is published to LiveKit and Egress records
  // server-side — the client uploads no videoChunk.
  sendVideoChunk?: (
    videoBlob: Blob,
    mimeType: string,
    role: 'user' | 'avatar'
  ) => void
  grantedPermissions?: GrantedPermissions
}

const constraints = {
  audio: {
    echoCancellation: true
  },
  video: {
    facingMode: { ideal: 'user' },
    frameRate: 12,
    height: { ideal: 480, max: 640 }
    // aspectRatio: 844 / 390,
    // width: { ideal: 390 },
    // height: { ideal: 844 }
  }
}

// Reduce a device label to a "brand" token shared between a camera and its
// matching microphone, so we can pair them up. macOS gives the camera and the
// microphone of the same physical device DIFFERENT groupIds (e.g. the Apple
// Studio Display camera enumerates as USB 15bc:0000 while its mic/speakers are
// 05ac:1118), so groupId pairing does not work there. The labels, however,
// share a brand prefix: "Studio Display Camera (15bc:0000)" and
// "Studio Display Microphone (05ac:1118)" both reduce to "studio display";
// "iPhone … Camera" / "iPhone … Microphone" both reduce to "iphone …".
//
// We strip the "Default - " prefix, any "(…)" groups (vendor ids, "(Built-in)")
// and the kind word (camera/microphone/…), then lowercase. Built-in devices
// have no shared brand ("FaceTime HD" vs "MacBook Pro") and intentionally fall
// through to the system default mic, which is the built-in mic anyway.
const brandToken = (label: string | undefined): string =>
  (label || '')
    .replace(/^Default\s*-\s*/i, '')
    .replace(/\s*\([^)]*\)\s*/g, ' ')
    .replace(/\b(camera|microphone|mic|speakers?|webcam|audio)\b/gi, '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase()

// Find a microphone that belongs to the same physical device as the opened
// camera, matching by brand token. Returns null when there is no confident
// match (built-in, audio-only setups), letting the OS default stand. When more
// than one device matches (e.g. two identical Studio Displays) we take the
// first — it does not matter which one records as long as one does.
const pickMicForCamera = (
  devices: MediaDeviceInfo[],
  cameraDeviceId: string | null
): string | null => {
  const camera = find(
    devices,
    (d) => d.kind === 'videoinput' && d.deviceId === cameraDeviceId
  )
  const token = brandToken(get(camera, 'label'))
  if (!token) return null

  const mic = find(
    devices,
    (d) =>
      d.kind === 'audioinput' &&
      d.deviceId !== 'default' &&
      d.deviceId !== 'communications' &&
      brandToken(d.label) === token
  )
  return get(mic, 'deviceId', null)
}

const StreamDataProvider: FC<Props> = ({
  children,
  onError,
  isLazy = false,
  onPermissionsError,
  sendVideoChunk,
  grantedPermissions
}) => {
  const [getStream, setStream] = useGetSet<MediaStream | null>(null)
  const [devices, setDevices] = useState<MediaDeviceInfo[]>([])
  const [selectedDeviceId, setSelectedDeviceId] = useState<string | null>(null)
  const [microphones, setMicrophones] = useState<MediaDeviceInfo[]>([])
  const [selectedMicId, setSelectedMicId] = useState<string | null>(null)
  // Currently selected device ids, kept in refs so createStream (a stable
  // useCallback) can build constraints without going stale.
  const selectedCameraIdRef = useRef<string | null>(null)
  const selectedMicIdRef = useRef<string | null>(null)
  // Last enumerated device list (with labels), used to pair camera <-> mic.
  const allDevicesRef = useRef<MediaDeviceInfo[]>([])
  const videoRecorderRef = useRef<RVideo>()
  const { setUserStream } = useContext(SessionVideoRecorderContext)
  const hiddenTimeoutRef = useRef<number | null>(null)

  const [controls, setControls] = useState<ControlT>({
    mic: true,
    camera: true
  })
  // Latest controls for createStream to re-apply mute/visibility after a
  // device switch (a fresh track defaults to enabled).
  const controlsRef = useRef(controls)
  controlsRef.current = controls

  // Surface a "we can't hear you" signal when the mic delivers no audio (OS/
  // hardware mute or a dead/wrong input device) so the UI can warn the user.
  // Only monitored while the mic is intentionally on.
  const micHealth = useMicHealth(getStream(), { active: controls.mic })

  const createStream = useCallback(
    async (
      contraints: MediaStreamConstraints,
      repairMic = true
    ): Promise<MediaStream> => {
      // stopping existing stream

      getStream()
        ?.getTracks()
        .forEach((t) => t.stop())

      // Pin to the currently selected camera/mic so every re-acquire
      // (visibility recapture, permission change, recaptureStream, mic pairing)
      // stays on the same physical devices instead of drifting to whatever the
      // OS picks as default (e.g. iPhone Continuity Camera on macOS). `{ ideal }`
      // (not `{ exact }`) lets the browser fall back to facingMode/default if a
      // pinned device is gone, avoiding OverconstrainedError. Only injected when
      // an id is actually pinned and that media is requested.
      let pinnedConstraints = contraints
      if (
        selectedCameraIdRef.current &&
        pinnedConstraints.video &&
        typeof pinnedConstraints.video !== 'boolean'
      ) {
        pinnedConstraints = {
          ...pinnedConstraints,
          video: {
            ...pinnedConstraints.video,
            deviceId: { ideal: selectedCameraIdRef.current }
          }
        }
      }
      if (
        selectedMicIdRef.current &&
        pinnedConstraints.audio &&
        typeof pinnedConstraints.audio !== 'boolean'
      ) {
        pinnedConstraints = {
          ...pinnedConstraints,
          audio: {
            ...pinnedConstraints.audio,
            deviceId: { ideal: selectedMicIdRef.current }
          }
        }
      }

      const newStream = await navigator.mediaDevices.getUserMedia(
        pinnedConstraints
      )
      jsLogger.log('StreamDataProvider: createStream ', pinnedConstraints)
      const videoTrack = newStream.getVideoTracks()[0]
      if (videoTrack) {
        jsLogger.log('Track state:', videoTrack.readyState) // should be "live"
        jsLogger.log('Muted:', videoTrack.muted)

        videoTrack.onmute = () => jsLogger.log('Track muted (no frames)')
        videoTrack.onunmute = () =>
          jsLogger.log('Track unmuted (frames flowing again)')
        videoTrack.onended = () => jsLogger.log('Track ended')
      }

      // A freshly captured track is enabled by default — re-apply the user's
      // mute/visibility choices so switching a device mid-session is seamless.
      const newAudioTrack = newStream.getAudioTracks()[0]
      if (newAudioTrack) newAudioTrack.enabled = controlsRef.current.mic
      if (videoTrack) videoTrack.enabled = controlsRef.current.camera

      // Pin the actually-opened camera so re-acquire stays on it, and reflect
      // it in the picker, when the user hasn't explicitly chosen one yet.
      if (!selectedCameraIdRef.current && videoTrack) {
        const camId = videoTrack.getSettings().deviceId
        if (camId) {
          selectedCameraIdRef.current = camId
          setSelectedDeviceId(camId)
        }
      }

      // Pair a microphone to the opened camera by label. macOS enumerates a
      // device's camera and mic under different groupIds, so the OS default mic
      // may be unrelated to the chosen camera (e.g. the built-in mic while the
      // camera is an external monitor's). When a better-matching mic exists,
      // pin it and re-acquire once (createStream re-injects it). Skipped on the
      // re-acquire (repairMic = false), when no mic is involved, and when the
      // user has already chosen a mic explicitly.
      if (
        repairMic &&
        contraints.audio &&
        videoTrack &&
        !selectedMicIdRef.current
      ) {
        try {
          allDevicesRef.current = await navigator.mediaDevices.enumerateDevices()
        } catch (error) {
          jsLogger.warn(
            'StreamDataProvider: enumerateDevices for mic pairing failed',
            { error }
          )
        }
        const pairedMicId = pickMicForCamera(
          allDevicesRef.current,
          selectedCameraIdRef.current
        )
        const currentMicId = get(newAudioTrack?.getSettings?.(), 'deviceId')
        if (pairedMicId && pairedMicId !== currentMicId) {
          selectedMicIdRef.current = pairedMicId
          setSelectedMicId(pairedMicId)
          jsLogger.log('StreamDataProvider: pairing microphone to camera', {
            pairedMicId,
            cameraDeviceId: selectedCameraIdRef.current
          })
          newStream.getTracks().forEach((t) => t.stop())
          return createStream(contraints, false)
        }
      }

      // No pairing happened — reflect the actually-active mic in the picker so
      // the radio is highlighted, when the user hasn't explicitly chosen one.
      if (!selectedMicIdRef.current && newAudioTrack) {
        const micId = newAudioTrack.getSettings().deviceId
        if (micId) {
          selectedMicIdRef.current = micId
          setSelectedMicId(micId)
        }
      }

      videoRecorderRef.current?.stop()
      // Record the user camera locally only when a recording sink is wired
      // (legacy client recording). In the server-side recording scheme no sink
      // is provided — the camera is published to LiveKit and Egress records it.
      if (contraints.video && sendVideoChunk) {
        videoRecorderRef.current = new RVideo(
          newStream,
          (duration: number) => {
            jsLogger.log('video recording is stopped', { duration })
          },
          (blob: Blob) => {
            const mimeType = videoRecorderRef.current?.mimeType || 'video/webm'
            sendVideoChunk(blob, mimeType, 'user')
          },
          onError,
          true
        )
      } else {
        videoRecorderRef.current = undefined
      }
      jsLogger.log(
        '>>>>>> StreamDataProvider, createStream function, set stream',
        { streamId: newStream.id }
      )
      setStream(newStream)
      setUserStream(newStream)

      return newStream
    },
    [onError, getStream, setStream]
  )

  const startStream = useCallback(async () => {
    try {
      const _isInlineRecordingSupported =
        !!window.MediaRecorder && !!navigator.mediaDevices
      jsLogger.log('StreamDataProvider: init', { _isInlineRecordingSupported })
      if (!_isInlineRecordingSupported) {
        jsLogger.warn(
          'StreamDataProvider: WARNINIG | RECORDER: inline recording is not supported'
        )
        onError('Inline recording is not supported')
        return
      }

      jsLogger.log('StreamDataProvider, requesting permissions')
      await requestMediaPermissions(constraints)
      jsLogger.log('StreamDataProvider, permissions granted')

      const mediaDevices = await navigator.mediaDevices.enumerateDevices()
      const cameras = mediaDevices.filter((d) => d.kind === 'videoinput')
      const mics = mediaDevices.filter((d) => d.kind === 'audioinput')
      jsLogger.log('StreamDataProvider: mediaDevices', mediaDevices)
      setDevices(cameras)
      setMicrophones(mics)
      // const firstDeviceId = get(cameras, [0, 'deviceId'], null)
      // jsLogger.log('StreamDataProvider: firstDeviceId', firstDeviceId)
      // setSelectedDeviceId(firstDeviceId)

      // const newConstaints = { ...constraints }
      // if (firstDeviceId) {
      //   set(newConstaints, 'video.deviceId', firstDeviceId)
      // }

      const newStream = await createStream(constraints)

      jsLogger.log(
        'StreamDataProvider | navigator.mediaDevices.getUserMedia done, stream created',
        { streamId: newStream.id }
      )
    } catch (error: any) {
      jsLogger.error('StreamDataProvider: error: ', {
        error
      })
      let specificError = PermissionErrorType.systemDenied // Default
      if (error.type === MediaPermissionsErrorType.Generic) {
        specificError = PermissionErrorType.systemDenied
      } else if (
        error.type === MediaPermissionsErrorType.SystemPermissionDenied
      ) {
        specificError = PermissionErrorType.systemDenied
      } else if (
        error.type === MediaPermissionsErrorType.UserPermissionDenied
      ) {
        specificError = PermissionErrorType.userDenied
      } else if (
        error.type === MediaPermissionsErrorType.CouldNotStartVideoSource
      ) {
        specificError = PermissionErrorType.trackError
      }
      onPermissionsError(specificError)
    }
  }, [])

  useEffect(() => {
    return () => {
      jsLogger.log('StreamDataProvider: unmounting, releasing stream')
      getStream()
        ?.getTracks()
        .forEach((t) => t.stop())
      if (videoRecorderRef.current) {
        videoRecorderRef.current?.stop()
        videoRecorderRef.current = undefined
        jsLogger.log('>>>>>> StreamDataProvider, unmounting, set stream null')

        setStream(null)
      }
    }
  }, [])

  useEffect(() => {
    const run = async () => {
      startStream()
    }
    if (!isLazy) {
      run()
    }
  }, [isLazy, startStream])

  useEffect(() => {
    if (!grantedPermissions) return
    const { camera, microphone } = grantedPermissions
    if (!camera && !microphone) return

    jsLogger.log('StreamDataProvider: grantedPermissions changed, recreating stream', {
      camera,
      microphone
    })

    const permConstraints: MediaStreamConstraints = {}
    if (microphone) {
      permConstraints.audio = constraints.audio
    }
    if (camera) {
      permConstraints.video = constraints.video
    }

    createStream(permConstraints).catch((error) => {
      jsLogger.error(
        'StreamDataProvider: failed to create stream after permissions change',
        { error }
      )
      onError(get(error, 'message', 'unknown'))
    })
  }, [grantedPermissions, createStream, onError])

  useEffect(() => {
    let wasHidden = document.hidden
    const hiddenTimeoutMs = 2 * 60 * 1000

    const handleVisibilityChange = () => {
      const isHidden = document.hidden
      const becameVisible = wasHidden && !isHidden
      wasHidden = isHidden

      if (isHidden) {
        if (hiddenTimeoutRef.current === null) {
          hiddenTimeoutRef.current = window.setTimeout(() => {
            if (!document.hidden) {
              if (hiddenTimeoutRef.current !== null) {
                clearTimeout(hiddenTimeoutRef.current)
                hiddenTimeoutRef.current = null
              }
              return
            }

            jsLogger.warn(
              'StreamDataProvider: hidden too long, stopping stream'
            )
            const stream = getStream()
            stream?.getTracks().forEach((t) => t.stop())
            videoRecorderRef.current?.stop()
            videoRecorderRef.current = undefined
            setStream(null)
            if (stream) {
              onError('Recording stopped due to inactivity')
            }
          }, hiddenTimeoutMs)
        }
        return
      }

      if (hiddenTimeoutRef.current !== null) {
        clearTimeout(hiddenTimeoutRef.current)
        hiddenTimeoutRef.current = null
      }

      if (!becameVisible) return

      const stream = getStream()
      if (!stream) return

      const tracks = stream.getTracks()
      const hasEndedTrack = tracks.some((t) => t.readyState === 'ended')

      if (hasEndedTrack) {
        jsLogger.warn(
          'StreamDataProvider: visibility restored with ended tracks, recreating stream'
        )
        createStream(constraints).catch((error) => {
          jsLogger.error(
            'StreamDataProvider: failed to recreate stream after visibility change',
            { error }
          )
          onError(get(error, 'message', 'unknown'))
        })
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      if (hiddenTimeoutRef.current !== null) {
        clearTimeout(hiddenTimeoutRef.current)
        hiddenTimeoutRef.current = null
      }
    }
  }, [createStream, getStream, onError, setStream])

  // Build constraints from the base config plus the currently selected camera
  // and microphone, so switching one device preserves the other.
  const buildConstraints = useCallback((): MediaStreamConstraints => {
    const newConstraints: MediaStreamConstraints = {
      audio: { ...(constraints.audio as MediaTrackConstraints) },
      video: { ...(constraints.video as MediaTrackConstraints) }
    }
    if (selectedCameraIdRef.current) {
      set(newConstraints, 'video.deviceId', selectedCameraIdRef.current)
    }
    if (selectedMicIdRef.current) {
      set(newConstraints, 'audio.deviceId', selectedMicIdRef.current)
    }
    return newConstraints
  }, [])

  const changeDeviceId = useCallback(
    async (deviceId: string) => {
      if (deviceId && selectedDeviceId !== deviceId) {
        selectedCameraIdRef.current = deviceId
        // Switching camera: drop the previous mic pairing so createStream
        // re-pairs a microphone to the newly selected camera.
        selectedMicIdRef.current = null
        setSelectedMicId(null)
        setSelectedDeviceId(deviceId)

        const newStream = await createStream(buildConstraints())
        jsLogger.log(
          'StreamDataProvider | camera changed, stream created',
          { streamId: newStream.id }
        )
      }
    },
    [selectedDeviceId, createStream, buildConstraints]
  )

  const changeMicId = useCallback(
    async (deviceId: string) => {
      if (deviceId && selectedMicId !== deviceId) {
        selectedMicIdRef.current = deviceId
        setSelectedMicId(deviceId)

        const newStream = await createStream(buildConstraints())
        jsLogger.log('StreamDataProvider | microphone changed, stream created', {
          streamId: newStream.id
        })
      }
    },
    [selectedMicId, createStream, buildConstraints]
  )

  const changeControls = useCallback(
    async (newControls: ControlT) => {
      if (
        newControls.mic !== controls.mic ||
        newControls.camera !== controls.camera
      ) {
        setControls(newControls)
        const stream = getStream()
        if (stream) {
          const audioTrack = stream
            .getTracks()
            .find((track) => track.kind === 'audio')
          if (audioTrack) {
            audioTrack.enabled = newControls.mic
          }
          const videoTrack = stream
            .getTracks()
            .find((track) => track.kind === 'video')
          if (videoTrack) {
            videoTrack.enabled = newControls.camera
          }
        }
      }
    },
    [controls, getStream]
  )

  const pauseRecordingLiveness = useCallback(() => {
    videoRecorderRef.current?.pauseRecordingLiveness()
  }, [])

  const resumeRecordingLiveness = useCallback(() => {
    videoRecorderRef.current?.resumeRecordingLiveness()
  }, [])

  const releaseStream = useCallback(() => {
    jsLogger.log('StreamDataProvider: releaseStream')
    const stream = getStream()
    stream?.getTracks().forEach((t) => t.stop())
    videoRecorderRef.current?.stop()
    videoRecorderRef.current = undefined
    setStream(null)
    setUserStream(undefined)
  }, [getStream, setStream, setUserStream])

  const recordExternalStream = useCallback(
    (externalStream: MediaStream) => {
      jsLogger.log('StreamDataProvider: recordExternalStream', {
        streamId: externalStream.id
      })
      videoRecorderRef.current?.stop()
      // Record locally only when a recording sink is wired (legacy client
      // recording). In the server-side scheme no sink is provided — Egress records.
      if (sendVideoChunk) {
        videoRecorderRef.current = new RVideo(
          externalStream,
          (duration: number) => {
            jsLogger.log('external video recording is stopped', { duration })
          },
          (blob: Blob) => {
            const mimeType = videoRecorderRef.current?.mimeType || 'video/webm'
            sendVideoChunk(blob, mimeType, 'user')
          },
          onError,
          false
        )
      } else {
        videoRecorderRef.current = undefined
      }
      setStream(externalStream)
      setUserStream(externalStream)
    },
    [onError, sendVideoChunk, setStream, setUserStream]
  )

  const recaptureStream = useCallback(async () => {
    jsLogger.log('StreamDataProvider: recaptureStream')
    try {
      await createStream(constraints)
    } catch (error: any) {
      jsLogger.error('StreamDataProvider: recaptureStream failed', { error })
      onError(get(error, 'message', 'unknown'))
    }
  }, [createStream, onError])

  // const providerValue = useMemo(() => {
  //   return {
  //     stream: getStream(),
  //     devices,
  //     selectedDeviceId,
  //     changeDeviceId,
  //     controls,
  //     changeControls,
  //     startStream
  //   }
  // }, [
  //   getStream,
  //   devices,
  //   selectedDeviceId,
  //   changeDeviceId,
  //   controls,
  //   changeControls,
  //   startStream
  // ])

  return (
    <StreamContext.Provider
      value={{
        stream: getStream(),
        devices,
        selectedDeviceId,
        changeDeviceId,
        microphones,
        selectedMicId,
        changeMicId,
        controls,
        changeControls,
        micHealth,
        startStream,
        pauseRecordingLiveness,
        resumeRecordingLiveness,
        releaseStream,
        recordExternalStream,
        recaptureStream
      }}
    >
      {children}
    </StreamContext.Provider>
  )
}

export default StreamDataProvider
